/**
 * Scope-bound drafts — what a scope change throws away besides the query cache (plan-014 A4,
 * FR12-AUTH-11: "discards scope-bound caches and drafts").
 *
 * Two kinds:
 *
 *   · **Kept in the tab's storage.** The New agent wizard keeps its answers in `sessionStorage` under
 *     its own key prefix (`routes/agents/new/wizard-draft.ts`, keyed by workspace). A draft of one
 *     organization must not be offered in another, so every key under a registered prefix is removed.
 *     The prefix is read FROM the wizard's own key function, never retyped here, so the two cannot
 *     drift. (The wizard's attached files sit in IndexedDB under the same key; the wizard reads them
 *     only through its draft entry, so with the entry gone no page offers them again.)
 *   · **Held in memory by a page.** A page with an unsent form registers a clearer with
 *     {@link registerScopeDraft}; the scope guard calls every clearer when the scope moves.
 */
import { draftKeyOf } from "../routes/agents/new/wizard-draft.js";

/**
 * The wizard's key prefix, derived from its key function: everything before the workspace segment.
 * A marker that cannot occur in a workspace id splits it off.
 */
function wizardDraftPrefix(): string {
  const marker = "\u0000";
  const [prefix] = draftKeyOf(marker, "").split(marker);
  if (prefix === undefined || prefix === "") throw new Error("the wizard's draft key has no prefix to clear by");
  return prefix;
}

/** Every `sessionStorage` key prefix that belongs to one scope. */
export const SCOPE_BOUND_STORAGE_PREFIX: readonly string[] = [wizardDraftPrefix()];

/** The slice of `Storage` the sweep uses. */
export interface DraftStorage {
  readonly length: number;
  key(index: number): string | null;
  removeItem(key: string): void;
}

/** Removes every key under a scope-bound prefix. Returns how many went. */
export function sweepScopeBoundStorage(storage: DraftStorage | null): number {
  if (storage === null) return 0;
  const doomed: string[] = [];
  try {
    for (let index = 0; index < storage.length; index += 1) {
      const key = storage.key(index);
      if (key !== null && SCOPE_BOUND_STORAGE_PREFIX.some((prefix) => key.startsWith(prefix))) doomed.push(key);
    }
    for (const key of doomed) storage.removeItem(key);
  } catch {
    // Storage that refuses to be read held nothing this sweep could have offered in the new scope.
    return 0;
  }
  return doomed.length;
}

const inMemoryDraft = new Set<() => void>();

/** A page's unsent draft, dropped when the scope moves. Returns the unregister. */
export function registerScopeDraft(clear: () => void): () => void {
  inMemoryDraft.add(clear);
  return () => {
    inMemoryDraft.delete(clear);
  };
}

/** The tab's `sessionStorage`, or `null` where the browser has none or refuses it. */
function tabStorage(): DraftStorage | null {
  try {
    // glossary-lint-disable-next-line session -- the DOM's own sessionStorage API, not a WeTalk session
    return typeof sessionStorage === "undefined" ? null : sessionStorage;
  } catch {
    return null;
  }
}

/** Drops every scope-bound draft: the registered in-memory ones and the tab's stored ones. */
export function dropScopeDrafts(storage: DraftStorage | null = tabStorage()): void {
  for (const clear of [...inMemoryDraft]) clear();
  sweepScopeBoundStorage(storage);
}
